// Helper functions for the marketplace admin dashboard

/**
 * Format a number as US currency
 * @param {number} amount - Amount in dollars
 * @returns {string} Formatted currency string (e.g., "$123.45")
 */
export const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

/**
 * Format an ISO date string to human-readable format
 * @param {string} isoString - ISO 8601 date string
 * @returns {string} Formatted date string (e.g., "Mar 1, 2026")
 */
export const formatDate = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format an ISO date string to include time
 * @param {string} isoString - ISO 8601 date string
 * @returns {string} Formatted date string with time (e.g., "Mar 1, 2026, 2:14 PM")
 */
export const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format ID with # prefix
 * @param {string} id - ID to format
 * @returns {string} Formatted ID (e.g., "#64abc123...")
 */
export const formatId = (id) => {
  if (!id) return '—';
  return `#${String(id)}`;
};

/**
 * Get status badge configuration
 * @param {string} status - Transfer status
 * @returns {object} Badge configuration with label and color
 */
export const getStatusConfig = (status) => {
  const configs = {
    completed: { label: 'Completed', color: '#07bc0c', bgColor: '#E6F6E6' },
    pending: { label: 'Pending', color: '#FFA500', bgColor: '#FFF3DA' },
    scheduled: { label: 'Scheduled', color: '#0072EE', bgColor: '#E6F1FD' },
    processing: { label: 'Processing', color: '#00BCD4', bgColor: '#E0F7FA' },
    failed: { label: 'Failed', color: '#EA1010', bgColor: '#FDE7E7' },
    on_hold: { label: 'On Hold', color: '#FF6B00', bgColor: '#FFE8D6' },
    reversed: { label: 'Reversed', color: '#9C27B0', bgColor: '#F3E5F5' },
    cancelled: { label: 'Cancelled', color: '#A6A6A6', bgColor: '#F2F2F2' },
  };
  
  return configs[status] || { label: status, color: '#545454', bgColor: '#F2F2F2' };
};

/**
 * Get order type label
 * @param {string} orderType - Order type
 * @returns {string} Order type label
 */
export const getOrderTypeLabel = (orderType) => {
  const labels = {
    Product: 'Product',
    Service: 'Service',
    Offer: 'Offer',
    autoship: 'Autoship',
    Tip: 'Tip',
  };
  
  return labels[orderType] || orderType;
};
